import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCateringData } from '../hooks/useCateringData.js'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'
import { formatMoney } from '../lib/format.js'  // summary card pipeline $
import CateringInquiryModal from './CateringInquiryModal.jsx'
import CateringQuoteModal from './CateringQuoteModal.jsx'
import CateringInquiryRow from './CateringInquiryRow.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import Toast from './Toast.jsx'

// Catering tab v2 — manager-side inbox of catering leads, quotes, and
// confirmed events. Layout:
//   - Summary card (top)
//   - Inline 4-status segmented control: New / Quoted / Confirmed / History
//   - Tab body: filtered inquiry list for the active tab
//
// The detail surface (modal) renders per-status sub-sections:
//   - status='quoted'    → CateringFollowupSection (AI draft follow-up)
//   - status='confirmed' → CateringImpactSection   (deterministic impact view)
//
// Status workflow is driven through useCateringData.updateInquiry,
// which auto-stamps quoted_at / confirmed_at / completed_at when the
// status flips. CateringScreen never calls supabase.update directly.

// Tabs definition. 'history' aggregates completed + cancelled + declined.
const TABS = [
  { id: 'new',       label: 'New' },
  { id: 'quoted',    label: 'Quoted' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'history',   label: 'History' },
]

export default function CateringScreen() {
  const {
    inquiries,
    byStatus,
    loading,
    createInquiry,
    updateInquiry,
    deleteInquiry,
  } = useCateringData()

  // currentRestaurantId is NULL for single-unit accounts (the inert
  // UnitContext contract). When a grouped user has a specific unit
  // selected it is that unit's id. We use it ONLY to scope this screen's
  // inquiries to the selected unit by assigned_restaurant_id. Single-unit
  // (null) → no filter → byte-identical to the pre-multi-unit behavior.
  const { currentRestaurantId } = useCurrentUnit()

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [aiQuoteFor, setAiQuoteFor] = useState(null)
  const [items, setItems] = useState([])
  const [toast, setToast] = useState('')
  const [activeTab, setActiveTab] = useState(null) // null until first compute

  // Unit-scoped status groups. When currentRestaurantId is null (single-
  // unit), this is byStatus untouched. When a grouped user has selected a
  // unit, each bucket is filtered to assigned_restaurant_id === that unit.
  // Filtering on assigned_restaurant_id (NOT restaurant_id) is intentional
  // — the assignment is the source of truth for which unit runs the event.
  const scopedByStatus = useMemo(() => {
    if (!currentRestaurantId) return byStatus
    const keep = (i) => i.assigned_restaurant_id === currentRestaurantId
    return {
      new:       byStatus.new.filter(keep),
      quoted:    byStatus.quoted.filter(keep),
      confirmed: byStatus.confirmed.filter(keep),
      completed: byStatus.completed.filter(keep),
      cancelled: byStatus.cancelled.filter(keep),
      declined:  byStatus.declined.filter(keep),
    }
  }, [byStatus, currentRestaurantId])

  // Pull a small slice of menu items so the AI Quote modal can build
  // line items out of the restaurant's actual catalog. Cached for the
  // life of this screen.
  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data, error } = await supabase
        .from('items')
        .select('id,name,category,unit,cost_per_unit')
        .eq('active', true)
        .order('name')
        .limit(60)
      if (!cancelled && !error) setItems(data ?? [])
    }
    load()
    return () => { cancelled = true }
  }, [])

  // History group — derived once per render.
  const historyList = useMemo(
    () => [...scopedByStatus.completed, ...scopedByStatus.cancelled, ...scopedByStatus.declined]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [scopedByStatus.completed, scopedByStatus.cancelled, scopedByStatus.declined],
  )

  // Per-tab counts for the segmented control labels.
  const tabCounts = useMemo(
    () => ({
      new:       scopedByStatus.new.length,
      quoted:    scopedByStatus.quoted.length,
      confirmed: scopedByStatus.confirmed.length,
      history:   historyList.length,
    }),
    [scopedByStatus.new.length, scopedByStatus.quoted.length, scopedByStatus.confirmed.length, historyList.length],
  )

  // Default tab: prefer the most-urgent non-empty bucket.
  const defaultTab = useMemo(() => {
    if (tabCounts.new > 0)       return 'new'
    if (tabCounts.quoted > 0)    return 'quoted'
    if (tabCounts.confirmed > 0) return 'confirmed'
    return 'history'
  }, [tabCounts.new, tabCounts.quoted, tabCounts.confirmed])

  // Lock the active tab to the default on first compute, but don't
  // overwrite a tab the user has explicitly chosen.
  useEffect(() => {
    if (activeTab == null) setActiveTab(defaultTab)
  }, [defaultTab, activeTab])

  // Sort the active tab's inquiries. Future events first for quoted /
  // confirmed; most recent first for new / history.
  const visibleInquiries = useMemo(() => {
    if (activeTab === 'history') return historyList
    if (activeTab === 'quoted' || activeTab === 'confirmed') {
      const list = activeTab === 'quoted' ? scopedByStatus.quoted : scopedByStatus.confirmed
      return [...list].sort((a, b) => {
        const da = a.event_date ? new Date(a.event_date) : new Date('9999-12-31')
        const db = b.event_date ? new Date(b.event_date) : new Date('9999-12-31')
        return da - db
      })
    }
    // 'new' tab: most recent first.
    return [...scopedByStatus.new].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }, [activeTab, scopedByStatus.new, scopedByStatus.quoted, scopedByStatus.confirmed, historyList])

  const newCount = scopedByStatus.new.length
  const activeCount = scopedByStatus.quoted.length + scopedByStatus.confirmed.length
  const pipelineValue =
    [...scopedByStatus.quoted, ...scopedByStatus.confirmed]
      .reduce((s, i) => s + Number(i.estimated_total || 0), 0)

  // Total inquiries in scope — drives the all-empty state. For single-unit
  // this equals inquiries.length; for a grouped unit it's the scoped count.
  const scopedTotal =
    scopedByStatus.new.length + scopedByStatus.quoted.length + scopedByStatus.confirmed.length +
    scopedByStatus.completed.length + scopedByStatus.cancelled.length + scopedByStatus.declined.length

  // Loading skeleton — match other screens. HOOK ORDERING: all hooks
  // above this early return per CLAUDE.md error #310.
  if (loading && inquiries.length === 0) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={3} className="mt-2" />
          </section>
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={4} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  async function handleCreate(payload) {
    // Multi-unit: attribute the new inquiry to the SELECTED unit so it is
    // both OWNED by and VISIBLE under that unit. This list filters on
    // `assigned_restaurant_id === currentRestaurantId` (line ~65), so without
    // an explicit assignment a freshly-created inquiry would be filtered out
    // of the very unit the operator created it on (it only surfaced in the
    // all-units roll-up). We also set restaurant_id so the unit-scoped AI
    // surfaces (proposal/follow-up) key off the right unit. Respect an
    // explicit assignment that group-create mode may already carry. Single-
    // unit (currentRestaurantId === null) → unchanged (DB trigger default).
    const scoped = currentRestaurantId
      ? {
          ...payload,
          restaurant_id: payload.restaurant_id ?? currentRestaurantId,
          assigned_restaurant_id: payload.assigned_restaurant_id ?? currentRestaurantId,
        }
      : payload
    await createInquiry(scoped)
    setToast(`Added inquiry from ${payload.contact_name?.trim() || 'customer'}.`)
  }

  async function handleSave(id, patch) {
    const updated = await updateInquiry(id, patch)
    // M1: resync `editing` so the modal reflects the post-transition
    // state (status, quoted_at/confirmed_at, etc.) without requiring
    // close+reopen. updateInquiry returns the .select().single() row.
    if (updated) setEditing(updated)
    setToast('Inquiry saved.')
    return updated
  }

  async function handleDelete(id) {
    await deleteInquiry(id)
    setToast('Inquiry deleted.')
  }

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">

        {/* ---------- Summary card ---------------------------- */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Catering pipeline summary"
        >
          <div className="text-xs font-medium text-black/55">
            Catering pipeline
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
            <div className="rounded-lg bg-blue-50 px-3 py-2">
              <div className="text-xs text-blue-900/70">New</div>
              <div className="text-2xl font-medium tabular-nums text-blue-700">
                {newCount}
              </div>
            </div>
            <div className="rounded-lg bg-amber-50 px-3 py-2">
              <div className="text-xs text-amber-900/70">Active</div>
              <div className="text-2xl font-medium tabular-nums text-amber-700">
                {activeCount}
              </div>
              <div className="text-xs text-amber-900/60 mt-0.5">
                quoted + confirmed
              </div>
            </div>
            <div className="rounded-lg bg-emerald-50 px-3 py-2">
              <div className="text-xs text-emerald-900/70">Pipeline $</div>
              <div className="text-2xl font-medium tabular-nums text-emerald-700">
                {formatMoney(pipelineValue)}
              </div>
              <div className="text-xs text-emerald-900/60 mt-0.5">
                estimated total
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-4 min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
          >
            + New inquiry
          </button>
        </section>

        {/* ---------- Inline segmented control ----------------- */}
        {/* Local, self-contained — the App-level TabBar is one consumer
            only and not a reusable pattern. Mobile (375px) scrolls
            horizontally; on wider screens 4 fit comfortably. */}
        <nav
          aria-label="Catering status filter"
          className="mx-4 mt-4 overflow-x-auto"
        >
          <div className="inline-flex min-w-full items-center gap-1 rounded-full bg-black/5 p-1">
            {TABS.map((t) => {
              const isActive = activeTab === t.id
              const count = tabCounts[t.id] ?? 0
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  aria-pressed={isActive}
                  className={[
                    'whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors',
                    // Tailwind config exposes amber.brand at 50/200/400/600/800/900 only
                    // — no 100/700 step. Use 200/800 for the filled active state.
                    isActive
                      ? 'bg-amber-brand-200 font-semibold text-amber-brand-800'
                      : 'text-black/60 hover:text-black/80',
                  ].join(' ')}
                >
                  {t.label} <span className="ml-1 text-xs tabular-nums opacity-75">({count})</span>
                </button>
              )
            })}
          </div>
        </nav>

        {/* ---------- Tab body --------------------------------- */}
        <section className="mx-4 mt-4" aria-label={`${activeTab || ''} inquiries`}>
          {visibleInquiries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-10 text-center text-sm text-black/60">
              {tabEmptyMessage(activeTab)}
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {visibleInquiries.map((i) => (
                <CateringInquiryRow
                  key={i.id}
                  inquiry={i}
                  onTap={() => setEditing(i)}
                  onAiQuote={() => setAiQuoteFor(i)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* Empty state — no inquiries at all in any bucket (in scope). */}
        {scopedTotal === 0 && (
          <div className="mx-4 mt-6 rounded-xl border border-dashed border-black/15 px-4 py-10 text-center text-sm text-black/60">
            <div className="text-base font-medium mb-1">No catering inquiries yet</div>
            <p>
              When customers ask about catering — by phone, email, or in person —
              tap <span className="font-medium text-blue-600">+ New inquiry</span> above to log it.
            </p>
          </div>
        )}
      </main>

      <CateringInquiryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        inquiry={null}
      />

      <CateringInquiryModal
        open={!!editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        onDelete={handleDelete}
        inquiry={editing}
      />

      <CateringQuoteModal
        open={!!aiQuoteFor}
        onClose={() => setAiQuoteFor(null)}
        inquiry={aiQuoteFor}
        items={items}
        onApplyToInquiry={async (patch, fullQuotePayload) => {
          // ATOMIC update: estimated_total + notes + status='quoted' +
          // quote_payload, all in one updateInquiry call. The hook
          // auto-stamps quoted_at on the status flip. One DB write means
          // the inquiry can never end up in a half-applied state.
          await updateInquiry(aiQuoteFor.id, {
            ...patch,                  // estimated_total, notes
            status: 'quoted',          // hook auto-stamps quoted_at
            quote_payload: fullQuotePayload,
          })
          setToast(`Quote applied to ${aiQuoteFor.contact_name}.`)
          // M3: jump the operator to the Quoted tab so the just-quoted
          // inquiry lands somewhere visible instead of disappearing
          // from "New" silently.
          setActiveTab('quoted')
        }}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}

// Per-tab empty state copy.
function tabEmptyMessage(tab) {
  switch (tab) {
    case 'new':       return 'No new inquiries waiting on you.'
    case 'quoted':    return 'No quotes out for response right now.'
    case 'confirmed': return 'No confirmed events yet.'
    case 'history':   return 'No past events in your history yet.'
    default:          return 'Nothing here yet.'
  }
}
